// src/components/admin/MemberListModal.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../../../css/admin/chats/MemberListModal.css';
import DsModal from '../../common/DsModal';
import DsButton from '../../common/DsButton';
import MemberListModalLayout from '../../common/chat/members/MemberListModalLayout';
import MemberListModalContent from '../../common/chat/members/MemberListModalContent';
import { AdminMemberProfileModal } from '../../common/chat/members/memberProfileModalVariants';
import useEventMembersVisibilityControl from '../../common/events/useEventMembersVisibilityControl';
import EventMembersVisibilityConfirmModal from '../../common/events/EventMembersVisibilityConfirmModal';
import {
  parsePartIds,
} from '../../common/chat/members/memberListUtils';
import {
  formatAdminName,
} from '../../common/chat/members/memberModalProfileUtils';
import {
  addAdminEventChatManualArtist,
  listCastingArtists,
  listAdminEventChatParticipants,
  updateAdminEventChatArtistAccess,
} from '../../../api/eventChat';
import { MemberArtistSection } from '../../common/chat/members/MemberListSectionParts';
import { resolveEventResponsibleAdminsForModal } from '../../common/chat/members/memberModalSharedDataUtils';
import useMemberModalEventState from '../../common/chat/members/useMemberModalEventState';
import useEventMemberModalData from '../../common/chat/members/useEventMemberModalData';
import useMemberListModalPresentation from '../../common/chat/members/useMemberListModalPresentation';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../common/ToastSystem';
import { resolveEventDetailPermission } from '../../../constants/eventDetailPermissions';
import DsSelect from '../../common/DsSelect';

const API_URL = process.env.REACT_APP_API_URL;
const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;
export default function MemberListModal({
  eventId,
  open,
  onClose,
  assignedAdmins = [],
  conciergePlanner = null,
  isConciergeEvent = false,
  isUnifiedEventGroupChat = false,
  currentEvent = null,
  groupEventRows = [],
  setCurrentEventState = null,
  setGroupEventsState = null,
}) {
  const { user } = useAuth();
  const { success, error: toastError } = useToast();
  const adminRole = String(user?.role || 'general').trim().toLowerCase() || 'general';
  const isTaskOnlyRole = adminRole === 'task_only';
  const adminPermissions = useMemo(() => {
    if (Array.isArray(user?.permissions)) return user.permissions;
    return user?.permissions ? [user.permissions] : [];
  }, [user?.permissions]);
  const canEditEventInfo = useMemo(
    () =>
      resolveEventDetailPermission({
        role: adminRole,
        permissions: adminPermissions,
        permissionKey: 'EVENT_EDIT_INFO',
      }),
    [adminPermissions, adminRole]
  );
  const canEditParticipants = useMemo(
    () =>
      canEditEventInfo &&
      resolveEventDetailPermission({
        role: adminRole,
        permissions: adminPermissions,
        permissionKey: 'EVENT_EDIT_PARTICIPANTS',
      }),
    [adminPermissions, adminRole, canEditEventInfo]
  );
  const eventReadOnlyTitle = isTaskOnlyRole
    ? 'タスク管理専用ロールではイベント情報を変更できません。'
    : 'このユーザー権限ではこの操作を実行できません。';
  const canViewParticipants = useMemo(
    () =>
      resolveEventDetailPermission({
        role: adminRole,
        permissions: adminPermissions,
        permissionKey: 'EVENT_VIEW_PARTICIPANTS',
      }),
    [adminPermissions, adminRole]
  );
  const canViewContractStatus = useMemo(
    () =>
      resolveEventDetailPermission({
        role: adminRole,
        permissions: adminPermissions,
        permissionKey: 'EVENT_VIEW_CONTRACT_STATUS',
      }),
    [adminPermissions, adminRole]
  );
  const canViewCastingSettings = useMemo(
    () =>
      resolveEventDetailPermission({
        role: adminRole,
        permissions: adminPermissions,
        permissionKey: 'EVENT_VIEW_CASTING_SETTINGS',
      }),
    [adminPermissions, adminRole]
  );
  const canViewCastingInfo = canViewCastingSettings || canViewParticipants || canViewContractStatus;
  const canViewCastingProgress = canViewParticipants || canViewContractStatus;

  const {
    loading,
    participantSnapshot,
    parts,
    castingInfo,
    progress,
    setParticipantSnapshot,
  } = useEventMemberModalData({
    open,
    apiUrl: API_URL,
    eventId,
    participantsUrl: `${API_URL}/api/admin/event-chat/participants/${eventId}`,
    includeCastingInfo: canViewCastingInfo,
    includeCastingProgress: canViewCastingProgress,
    errorLabel: 'member list fetch error',
  });
  const primaryArtistIds = useMemo(() => {
    if (!participantSnapshot || !Array.isArray(participantSnapshot?.artists)) return null;
    const separatelyListedIds = new Set(
      [
        ...(Array.isArray(participantSnapshot?.manual_chat_artists)
          ? participantSnapshot.manual_chat_artists
          : []),
        ...(Array.isArray(participantSnapshot?.concierge_planners)
          ? participantSnapshot.concierge_planners
          : []),
      ]
        .map((artist) => String(artist?.artist_id || artist?.id || '').trim())
        .filter(Boolean)
    );
    return new Set(
      participantSnapshot.artists
        .map((artist) => String(artist?.artist_id || artist?.id || '').trim())
        .filter((artistId) => artistId && !separatelyListedIds.has(artistId))
    );
  }, [participantSnapshot]);
  const {
    memberPartScopeEventUuid,
    setMemberPartScopeEventUuid,
    memberPartScopeTabs,
    resolveAdminMembershipLabel,
    resolveConciergeMembershipLabel,
    resolveArtistMembershipLabel,
    decidedSections,
    undecidedList,
  } = useMemberModalEventState({
    open,
    eventId,
    participantSnapshot,
    parts,
    castingInfo,
    progress,
    includedArtistIds: primaryArtistIds,
    artistsVisibleOnly: false,
    parsePartIdsFn: parsePartIds,
    useSnapshotArtistsForUnified: isUnifiedEventGroupChat,
    isUnifiedEventGroupChat,
  });

  const {
    profileNode,
    conciergeMembers,
    avatarStyle,
    avatarInitial,
    adminAvatarStyle,
    adminInitial,
    openAdminProfile,
    openArtistProfile,
  } = useMemberListModalPresentation({
    apiUrl: API_URL,
    artistProfilePath: ARTIST_PROFILE_IMAGE_PATH,
    adminProfilePath: ADMIN_PROFILE_IMAGE_PATH,
    isConciergeEvent,
    conciergePlanner,
    resolveArtistMembershipLabel,
    resolveAdminMembershipLabel,
    resolveConciergeMembershipLabel,
    variant: 'admin',
  });

  const currentAssignmentsForView = useMemo(
    () =>
      resolveEventResponsibleAdminsForModal({
        participantSnapshot,
        assignedAdmins,
      }),
    [assignedAdmins, participantSnapshot]
  );
  const {
    membersVisibleToArtists,
    membersVisibilitySaving,
    membersVisibilityButtonLabel,
    membersVisibilityConfirmState,
    closeMembersVisibilityConfirmModal,
    handleMembersVisibilityConfirmSingle,
    handleMembersVisibilityConfirmGroupAll,
    handleMembersVisibilityToggle,
  } = useEventMembersVisibilityControl({
    apiUrl: API_URL,
    eventId,
    currentEvent,
    groupEventRows,
    hasGroupEvents: Array.isArray(groupEventRows) && groupEventRows.length > 1,
    canEditParticipants,
    readOnlyTitle: eventReadOnlyTitle,
    setCurrentEventState,
    setGroupEventsState,
  });

  const [accessSavingArtistIds, setAccessSavingArtistIds] = useState([]);
  const [manualArtistModalOpen, setManualArtistModalOpen] = useState(false);
  const [manualArtistCandidates, setManualArtistCandidates] = useState([]);
  const [manualArtistLoading, setManualArtistLoading] = useState(false);
  const [manualArtistQuery, setManualArtistQuery] = useState('');
  const [manualArtistPage, setManualArtistPage] = useState(0);
  const [manualArtistError, setManualArtistError] = useState('');

  const withSavingArtistId = async (artistId, task) => {
    const targetId = String(artistId || '').trim();
    if (!targetId) return;
    setAccessSavingArtistIds((prev) => (prev.includes(targetId) ? prev : [...prev, targetId]));
    try {
      await task();
    } finally {
      setAccessSavingArtistIds((prev) => prev.filter((id) => id !== targetId));
    }
  };

  const updateSnapshotFromResponse = (payload) => {
    if (payload && typeof payload === 'object') {
      setParticipantSnapshot(payload);
    }
  };

  const handleArtistChatAccessModeChange = async (artistId, enabled) => {
    if (!canEditParticipants) return;
    const row = [participantSnapshot?.artists, participantSnapshot?.contracted_no_access_artists, participantSnapshot?.concierge_planners, participantSnapshot?.manual_chat_artists]
      .flatMap((values) => Array.isArray(values) ? values : []).find((artist) => String(artist.artist_id) === String(artistId));
    await withSavingArtistId(artistId, async () => {
      try {
        const data = await updateAdminEventChatArtistAccess(eventId, artistId, { enabled, revision: Number(row?.chat_access_revision || 0) });
        if (data?.success === false) {
          throw new Error(data?.message || 'チャット参加権限の更新に失敗しました。');
        }
        updateSnapshotFromResponse(data?.snapshot || null);
        success(enabled ? 'チャットをオンにしました' : 'チャットをオフにしました');
      } catch (error) {
        if (error?.status === 409) {
          try { setParticipantSnapshot(await listAdminEventChatParticipants(eventId)); } catch {}
        }
        toastError(error?.message || 'チャット参加権限の更新に失敗しました。');
      }
    });
  };

  useEffect(() => {
    if (!manualArtistModalOpen || !open) return;
    let active = true;
    setManualArtistLoading(true);
    setManualArtistError('');
    const timer = setTimeout(() => listCastingArtists({ lite: 1, q: manualArtistQuery.trim(), limit: 50, offset: manualArtistPage * 50 })
      .then((data) => {
        if (!active) return;
        setManualArtistCandidates(Array.isArray(data?.artists) ? data.artists : []);
      })
      .catch((error) => {
        if (!active) return;
        setManualArtistCandidates([]);
        setManualArtistError(error?.message || '候補アーティストの取得に失敗しました。');
      })
      .finally(() => {
        if (active) setManualArtistLoading(false);
      }), 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [manualArtistModalOpen, open, manualArtistQuery, manualArtistPage]);

  const knownArtistIds = useMemo(() => {
    const values = [
      ...(Array.isArray(participantSnapshot?.artists) ? participantSnapshot.artists : []),
      ...(Array.isArray(participantSnapshot?.contracted_no_access_artists)
        ? participantSnapshot.contracted_no_access_artists
        : []),
      ...(Array.isArray(participantSnapshot?.concierge_planners)
        ? participantSnapshot.concierge_planners
        : []),
    ];
    return new Set(
      values
        .filter((artist) => artist?.has_chat_access === true)
        .map((artist) => String(artist?.artist_id || artist?.id || '').trim())
        .filter(Boolean)
    );
  }, [participantSnapshot]);

  const filteredManualArtistCandidates = useMemo(() => {
    return (Array.isArray(manualArtistCandidates) ? manualArtistCandidates : []).filter((artist) => {
      const id = String(artist?.artist_id || '').trim();
      return id && !knownArtistIds.has(id);
    });
  }, [knownArtistIds, manualArtistCandidates]);

  const handleManualArtistAdd = async (artistId) => {
    if (!canEditParticipants) return;
    await withSavingArtistId(artistId, async () => {
      try {
        const data = await addAdminEventChatManualArtist(eventId, { artistId });
        if (data?.success === false) {
          throw new Error(data?.message || '参加アーティストの追加に失敗しました。');
        }
        updateSnapshotFromResponse(data?.snapshot || null);
        setManualArtistModalOpen(false);
        setManualArtistQuery('');
        success('チャットに追加しました');
      } catch (error) {
        toastError(error?.message || '参加アーティストの追加に失敗しました。');
      }
    });
  };

  const mapSnapshotArtistRow = useCallback((artist, { showMembershipLabel = true } = {}) => {
    const artistId = artist?.artist_id || artist?.id;
    return {
      id: artistId,
      artist_id: artistId,
      name: artist?.artist_name || artist?.name || artist?.artist_name_en || artist?.name_en || 'アーティスト',
      pic: artist?.artist_profile_picture || artist?.profile_picture || null,
      gender: artist?.artist_gender || artist?.gender || null,
      isChief: !!artist?.is_chief,
      isConcertmaster: !!artist?.is_concertmaster,
      customRoles: Array.isArray(artist?.custom_roles) ? artist.custom_roles : [],
      eventMembershipLabel: showMembershipLabel ? resolveArtistMembershipLabel(artist) : '',
      chat_access_source: artist?.chat_access_source || 'none',
      has_chat_access: artist?.has_chat_access === true,
      has_any_contract: artist?.has_any_contract === true,
      has_auto_chat_access: artist?.has_auto_chat_access === true,
      auto_chat_access_source: artist?.auto_chat_access_source || 'none',
      has_contract_access: artist?.has_contract_access === true,
      has_contract_chat_disabled: artist?.has_contract_chat_disabled === true,
      manual_enabled: artist?.manual_enabled === true,
      has_manual_override: artist?.has_manual_override === true,
      manual_override_mode: artist?.manual_override_mode || null,
    };
  }, [resolveArtistMembershipLabel]);

  const contractedNoAccessSection = useMemo(() => {
    const members = (Array.isArray(participantSnapshot?.contracted_no_access_artists)
      ? participantSnapshot.contracted_no_access_artists
      : []
    ).map(mapSnapshotArtistRow);
    return members.length
      ? {
          id: 'contracted-no-access',
          title: 'チャットがオフの参加者',
          titleShort: '',
          members,
        }
      : null;
  }, [mapSnapshotArtistRow, participantSnapshot]);

  const manualChatSection = useMemo(() => {
    const members = (Array.isArray(participantSnapshot?.manual_chat_artists)
      ? participantSnapshot.manual_chat_artists
      : []
    ).map((artist) => mapSnapshotArtistRow(artist, { showMembershipLabel: false }));
    return members.length
      ? {
          id: 'manual-chat-artists',
          title: 'チャットだけ利用する人',
          titleShort: '',
          members,
        }
      : null;
  }, [mapSnapshotArtistRow, participantSnapshot]);

  const renderChatAccessControl = (member) => {
    const artistId = String(member?.artist_id || member?.id || '').trim();
    if (!artistId) return null;
    const saving = accessSavingArtistIds.includes(artistId);
    const enabled = member.has_chat_access === true;
    return (
      <div
        className="member-modal__access-control"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <DsSelect
          className="member-modal__access-select"
          value={enabled ? 'on' : 'off'}
          disabled={!canEditParticipants || saving}
          title={!canEditParticipants ? eventReadOnlyTitle : 'チャット'}
          onChange={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleArtistChatAccessModeChange(artistId, event.target.value === 'on');
          }}
        >
          <option value="on">オン</option>
          <option value="off">オフ</option>
        </DsSelect>
      </div>
    );
  };

  if (!open) return null;
  const isAllMemberPartScope = String(memberPartScopeEventUuid) === 'all';
  const memberHeaderActions = (
    <>
      <DsButton
        variant="ghost"
        size="sm"
        className="member-modal__header-action"
        onClick={() => setManualArtistModalOpen(true)}
        disabled={!canEditParticipants}
        title={!canEditParticipants ? eventReadOnlyTitle : '契約なし参加メンバーを追加'}
      >
        <i className="fa-solid fa-user-plus" aria-hidden="true" />
        <span>チャットに追加</span>
      </DsButton>
      <DsButton
        variant="ghost"
        size="sm"
        className={`member-modal__header-action${membersVisibleToArtists ? ' is-visible' : ''}`}
        onClick={handleMembersVisibilityToggle}
        disabled={!canEditParticipants || membersVisibilitySaving}
        loading={membersVisibilitySaving}
        aria-pressed={membersVisibleToArtists}
        aria-label={membersVisibleToArtists ? '参加者を非公開にする' : '参加者を公開する'}
        title={!canEditParticipants ? eventReadOnlyTitle : '公開設定を変更'}
      >
        <i className={`fa-solid ${membersVisibleToArtists ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
        <span>{membersVisibilityButtonLabel}</span>
      </DsButton>
    </>
  );

  return (
    <MemberListModalLayout
      onClose={onClose}
      headerActions={memberHeaderActions}
      loading={loading}
      loadingLabel="読み込み中..."
      profileNode={profileNode}
      profilePlacement="outside"
    >
      <MemberListModalContent
        admins={currentAssignmentsForView}
        onAdminClick={openAdminProfile}
        adminKeyResolver={(admin, idx) => (
          admin.admin_user_id
          || admin.id
          || admin.artist_id
          || admin.username
          || `${formatAdminName(admin)}-${idx}`
        )}
        formatAdminName={formatAdminName}
        resolveAdminMembershipLabel={resolveAdminMembershipLabel}
        adminAvatarStyle={adminAvatarStyle}
        adminInitial={adminInitial}
        plannerMembers={conciergeMembers}
        showPlanner={isConciergeEvent}
        onPlannerClick={(planner) => {
          if (planner?.payload) {
            openArtistProfile(planner.payload);
          }
        }}
        avatarStyle={avatarStyle}
        avatarInitial={avatarInitial}
        responsibleEmptyText="未設定"
        plannerEmptyText="企画者が設定されていません"
        showPartScopeTabs
        partScopeTabs={memberPartScopeTabs}
        activePartScopeValue={memberPartScopeEventUuid}
        onChangePartScope={setMemberPartScopeEventUuid}
        partScopeKeyPrefix="member-scope"
        showArtistMembers
        isAllMemberPartScope={isAllMemberPartScope}
        onArtistClick={openArtistProfile}
        decidedSections={decidedSections}
        undecidedList={undecidedList}
        emptyText="参加メンバーがいません"
        renderArtistMemberTrailing={renderChatAccessControl}
      />
      {contractedNoAccessSection ? (
        <MemberArtistSection
          section={contractedNoAccessSection}
          onArtistClick={openArtistProfile}
          avatarStyle={avatarStyle}
          avatarInitial={avatarInitial}
          sectionClassName="member-modal__section"
          titleClassName="member-modal__section-title"
          listClassName="member-modal__list"
          rowClassName="member-modal__row"
          avatarClassName="member-modal__avatar"
          infoClassName="member-modal__info"
          nameClassName="member-modal__name"
          membershipClassName="member-modal__event-membership"
          badgesClassName="member-modal__badges"
          concertmasterBadgeClassName="member-modal__concertmaster-badge"
          chiefBadgeClassName="member-modal__chief-badge"
          roleBadgeClassName="member-modal__role-badge"
          renderTrailing={renderChatAccessControl}
        />
      ) : null}
      {manualChatSection ? (
        <MemberArtistSection
          section={manualChatSection}
          onArtistClick={openArtistProfile}
          avatarStyle={avatarStyle}
          avatarInitial={avatarInitial}
          sectionClassName="member-modal__section"
          titleClassName="member-modal__section-title"
          listClassName="member-modal__list"
          rowClassName="member-modal__row"
          avatarClassName="member-modal__avatar"
          infoClassName="member-modal__info"
          nameClassName="member-modal__name"
          membershipClassName="member-modal__event-membership"
          badgesClassName="member-modal__badges"
          concertmasterBadgeClassName="member-modal__concertmaster-badge"
          chiefBadgeClassName="member-modal__chief-badge"
          roleBadgeClassName="member-modal__role-badge"
          renderTrailing={renderChatAccessControl}
        />
      ) : null}
      <EventMembersVisibilityConfirmModal
        open={membersVisibilityConfirmState.open}
        saving={membersVisibilitySaving}
        nextVisible={membersVisibilityConfirmState.nextVisible}
        showGroupChoices={membersVisibilityConfirmState.showGroupChoices}
        unpublishedEventLabels={membersVisibilityConfirmState.unpublishedEventLabels}
        currentEventLabel={membersVisibilityConfirmState.currentEventLabel}
        onClose={closeMembersVisibilityConfirmModal}
        onConfirmGroupAll={handleMembersVisibilityConfirmGroupAll}
        onConfirmSingle={handleMembersVisibilityConfirmSingle}
      />
      {manualArtistModalOpen ? (
        <DsModal
          open={manualArtistModalOpen}
          onClose={() => setManualArtistModalOpen(false)}
          title="チャットに追加"
          size="sm"
          className="member-modal__subdialog"
        >
            <div className="member-modal__subbody">
              <input
                type="text"
                className="member-modal__search-input"
                value={manualArtistQuery}
                onChange={(event) => { setManualArtistQuery(event.target.value); setManualArtistPage(0); }}
                placeholder="名前で検索"
              />
              {manualArtistLoading ? <div className="member-modal__empty">読み込み中...</div> : null}
              {!manualArtistLoading && manualArtistError ? (
                <div className="member-modal__empty">{manualArtistError}</div>
              ) : null}
              {!manualArtistLoading && !manualArtistError ? (
                <ul className="member-modal__candidate-list">
                  {filteredManualArtistCandidates.map((artist) => {
                    const artistId = String(artist?.artist_id || '').trim();
                    const saving = accessSavingArtistIds.includes(artistId);
                    return (
                      <li key={artistId} className="member-modal__candidate-row">
                        <div className="member-modal__candidate-text">
                          <div className="member-modal__name">{artist?.name || 'アーティスト'}</div>
                          {artist?.name_en ? (
                            <div className="member-modal__responsible-meta">{artist.name_en}</div>
                          ) : null}
                        </div>
                        <DsButton
                          type="button"
                          className="member-modal__toolbar-button"
                          disabled={saving}
                          onClick={() => handleManualArtistAdd(artistId)}
                        >
                          {saving ? '追加中…' : '追加'}
                        </DsButton>
                      </li>
                    );
                  })}
                  {filteredManualArtistCandidates.length === 0 ? (
                    <li className="member-modal__empty">追加可能なアーティストがいません</li>
                  ) : null}
                </ul>
              ) : null}
              <DsButton disabled={manualArtistLoading || manualArtistPage === 0} onClick={() => setManualArtistPage((page) => page - 1)}>前へ</DsButton>
              <DsButton disabled={manualArtistLoading || manualArtistCandidates.length < 50} onClick={() => setManualArtistPage((page) => page + 1)}>次へ</DsButton>
            </div>
        </DsModal>
      ) : null}
    </MemberListModalLayout>
  );
}

export const MemberProfileModal = AdminMemberProfileModal;
